import { FC } from 'react';
import { DateFormats, DateOrTime, formatDateTime, formatNumber, TimeFormats } from '@nextgen-web-framework/core';
import {
  type TransactionEntry,
  PaymentMethodId,
  AccountBalanceEntryEntryTypeEnum,
} from '@commerce-payment-webcomponents/sdk';
import { useTranslation, FAIcon } from '@nextgen-web-framework/all';
import { ExpiringBalanceDetails } from '@commerce-payment-webcomponents/sdk/services/loyaltyModels/pdp/models/ExpiringBalanceDetails';
import { ActiveCreditVoucherEntry } from '@commerce-payment-webcomponents/sdk/services/models/ActiveCreditVoucherEntry';
import styles from './DrawerTransactionList.module.scss';

const DrawerTransactionList: FC<{
  transaction: TransactionEntry & ExpiringBalanceDetails & ActiveCreditVoucherEntry;
  paymentId: string;
  fromExpiringSoon?: boolean;
  fromMultiVoucher?: boolean;
  drawerHandler?: () => void;
}> = ({ transaction, paymentId, fromExpiringSoon, fromMultiVoucher, drawerHandler }) => {
  const t = useTranslation();
  const isCVMultiVoucher = !!transaction?.creditVoucherList?.length;

  const getStatusLabels = (paymentMethodId = ''): { [key: string]: string } => {
    switch (paymentMethodId) {
      case PaymentMethodId.OnAccountBalance:
        return {
          CREDIT: t('payment.accountHistory.filterTabs.credited'),
          DEBIT: t('payment.accountHistory.filterTabs.debited'),
          CAPTURE: t('payment.accountHistory.filterTabs.debited'),
          REFUND: t('payment.accountHistory.filterTabs.credited'),
        };
      case PaymentMethodId.AmwayPoints:
        return {
          CREDIT: t('payment.accountHistory.filterTabs.earned'),
          DEBIT: t('payment.accountHistory.filterTabs.redeemed'),
          EXPIRE: t('payment.accountHistory.filterTabs.expired'),
        };
      default:
        return {
          CREDIT: t('payment.accountHistory.filterTabs.received'),
          DEBIT: t('payment.accountHistory.filterTabs.redeemed'),
        };
    }
  };
  const getEntryStatusClass = (entryType = '') => {
    switch (entryType) {
      case AccountBalanceEntryEntryTypeEnum.Credit:
        return styles['success'];
      case AccountBalanceEntryEntryTypeEnum.Debit:
        return styles['error'];
      case AccountBalanceEntryEntryTypeEnum.Expire:
        return styles['error'];
      case AccountBalanceEntryEntryTypeEnum.Capture:
        return styles['error'];
      case AccountBalanceEntryEntryTypeEnum.Refund:
        return styles['success'];
      default:
        return styles['warning'];
    }
  };

  const getPlusMinus = (entryType = '') => {
    switch (entryType) {
      case AccountBalanceEntryEntryTypeEnum.Credit:
        return '+';
      case AccountBalanceEntryEntryTypeEnum.Debit:
        return '-';
      case AccountBalanceEntryEntryTypeEnum.Expire:
        return '-';
      case AccountBalanceEntryEntryTypeEnum.Refund:
        return '+';
      case AccountBalanceEntryEntryTypeEnum.Capture:
        return '-';
      default:
        return '';
    }
  };

  const getComment = (refType = '', refId = '') => {
    switch (refType) {
      case 'PROMOTION':
        return `${t('payment.accountHistory.CreditVoucher.promotionId')} ${refId ?? ''}`;
      case 'ORDER':
        return `${t('payment.accountHistory.CreditVoucher.orderId')} ${refId ?? ''}`;
      case 'BALANCEEXPIRED':
        return `${t('payment.accountHistory.CreditVoucher.balanceExpired')} ${refId ?? ''}`;
      case 'PAYMENT':
        return `${t('payment.accountHistory.CreditVoucher.paymentId')} ${refId ?? ''}`;
      default:
        return '';
    }
  };

  const getLabels = (isExpiring?: boolean, isCreditVoucher?: boolean) => {
    if (fromMultiVoucher && isCreditVoucher) {
      return <span className={styles['status']}>{t('payment.accountHistory.filterTabs.redeemed')}</span>;
    } else {
      if (isExpiring && isCreditVoucher) {
        return <span className={styles['status']}>{t('payment.accountHistory.filterTabs.received')}</span>;
      } else if (isExpiring && !isCreditVoucher) {
        return <span className={styles['status']}>{t('payment.accountHistory.Expiring')}</span>;
      } else {
        return <span className={styles['status']}>{statusLabels}</span>;
      }
    }
  };

  const statusLabels = getStatusLabels(paymentId)[transaction.entryType || ''];
  const TrxnDate = transaction.entryDate || transaction.expiringDate || transaction.createdAt;

  const creditVoucherBalanceSection = () => {
    if (paymentId === PaymentMethodId.CreditVoucher && fromExpiringSoon) {
      return (
        <div className={styles['transaction-availableBalance-section']}>
          {fromMultiVoucher
            ? t('payment.accountHistory.CreditVoucher.voucherNo')
            : t('payment.accountHistory.CreditVoucher.balance')}
          :{' '}
          <span>
            {fromMultiVoucher
              ? transaction.creditVoucherId
              : getUpdatedPrice(transaction.availableValue ?? transaction.value ?? 0)}
          </span>
        </div>
      );
    } else if (paymentId === PaymentMethodId.CreditVoucher) {
      return (
        <div className={styles['comment']}>
          {transaction?.entryType === AccountBalanceEntryEntryTypeEnum.Credit
            ? `${t('payment.accountHistory.CreditVoucher.voucherNo')} ${
                transaction.creditVoucherList?.[0]?.creditVoucherId ?? ''
              } ${transaction.creditVoucherId ?? ''}`
            : `${t('payment.accountHistory.CreditVoucher.orderId')} ${transaction?.refId ?? ''}`}
        </div>
      );
    } else {
      return '';
    }
  };

  const dateTimeFormatter = (
    date?: string,
    dateFormat?: DateFormats,
    timeFormat?: TimeFormats,
    dateOrTime?: DateOrTime,
  ) => {
    const configs = ConfigService.getConfigs();

    if (!date) {
      return '';
    }
    return formatDateTime(
      date,
      { date: dateFormat, time: timeFormat },
      false,
      configs.NEXT_PUBLIC_TIMEZONE as string,
      undefined,
      dateOrTime,
    );
  };
  const getUpdatedPrice = (price: number) => {
    const typedPrice = price?.toString();
    const formattedPrice = typedPrice?.includes('.') ? Math.round(parseFloat(typedPrice) * 100) / 100 : price;
    const newPrice = formatNumber(formattedPrice as number);

    return newPrice;
  };

  return (
    <div className={styles['drawerTransaction-row']}>
      <div className={styles['message-with-date']}>
        <div className={styles['text']}>
          {getLabels(fromExpiringSoon, paymentId === PaymentMethodId.CreditVoucher)}
          {!fromMultiVoucher && t('payment.accountHistory.on')} {''}
          {TrxnDate ? dateTimeFormatter(TrxnDate, DateFormats.LONG_DATE, undefined, DateOrTime.DATE_ONLY) : ''}
        </div>
        {fromMultiVoucher ? (
          <div className={`${styles['amount']} ${getEntryStatusClass(AccountBalanceEntryEntryTypeEnum.Debit)}`}>
            {getPlusMinus(AccountBalanceEntryEntryTypeEnum.Debit)}

            {getUpdatedPrice(transaction.value ?? 0)}
          </div>
        ) : fromExpiringSoon && paymentId === PaymentMethodId.CreditVoucher ? (
          <div className={`${styles['amount']} ${getEntryStatusClass(AccountBalanceEntryEntryTypeEnum.Credit)}`}>
            {getPlusMinus(AccountBalanceEntryEntryTypeEnum.Credit)}

            {getUpdatedPrice(transaction.issuedValue ?? 0)}
          </div>
        ) : (
          <div
            className={`${styles['amount']} ${isCVMultiVoucher && styles['multi-voucher']} ${
              fromExpiringSoon && paymentId !== PaymentMethodId.CreditVoucher
                ? styles['warning']
                : getEntryStatusClass(transaction.entryType)
            }`}
          >
            {getPlusMinus(transaction.entryType)}

            {getUpdatedPrice((transaction.amount as unknown as number) || (transaction.issuedValue as number))}
            {isCVMultiVoucher && transaction?.entryType != AccountBalanceEntryEntryTypeEnum.Credit && (
              <FAIcon
                iconName="angle-right"
                data-testid="multi-voucher-arrow"
                className={styles['multi-voucher-arrow']}
                onClick={() => drawerHandler?.()}
              />
            )}
          </div>
        )}
      </div>
      <div className={styles['transaction-remarks-section']}>
        {paymentId !== PaymentMethodId.CreditVoucher &&
          transaction?.entryType !== AccountBalanceEntryEntryTypeEnum.Expire && (
            <div className={styles['comment']}>
              {transaction.remarks || getComment(transaction.refType?.toUpperCase(), transaction.refId)}
            </div>
          )}
        {creditVoucherBalanceSection()}
      </div>
    </div>
  );
};

export default DrawerTransactionList;
